import { useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { AuthScaffold } from './AuthScaffold'
import { OAuthButtons, OrDivider, useOAuthProviders } from './OAuthButtons'
import { destinationAfterSignIn, safeReturnTo } from './afterAuth'
import { useAuth } from '@/auth/AuthProvider'
import { FxAlert, FxButton, FxTextField } from '@/components/fx'
import { ApiError } from '@/lib/api'
import { pendingUpload } from '@/lib/pendingUpload'

export function LoginPage() {
  const { login, signInWithGoogleToken } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [params] = useSearchParams()
  const { data: providers } = useOAuthProviders()

  const from = (location.state as { from?: string } | null)?.from ?? null
  const next = params.get('next')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function run(action: () => Promise<void>) {
    setBusy(true)
    setError(null)
    try {
      await action()
      // Read the held file now rather than from a cached query: it expires
      // after an hour, and sending someone to the analysis step with nothing
      // to analyse is a dead end.
      const held = await pendingUpload.take()
      navigate(destinationAfterSignIn({ pendingUpload: held != null, next, from }), {
        replace: true,
      })
    } catch (cause) {
      setError(ApiError.from(cause).message)
      setBusy(false)
    }
  }

  // Where the gateway's redirect flow should come back to. It must be a path
  // on this origin; the gateway drops anything else and the destination is
  // then silently lost.
  const returnTo = safeReturnTo(next) ?? safeReturnTo(from) ?? undefined

  return (
    <AuthScaffold
      title="Willkommen zurück"
      footer={
        <p className="text-body text-text">
          Du hast noch keinen Account?
          <br />
          <Link className="font-semibold text-primary-text no-underline" to="/register">
            Hier registrieren →
          </Link>
        </p>
      }
    >
      {error && (
        <FxAlert tone="danger" className="text-left">
          {error}
        </FxAlert>
      )}

      <form
        className="flex flex-col gap-7 text-left"
        onSubmit={(event) => {
          event.preventDefault()
          void run(() => login(email, password))
        }}
      >
        <div>
          {/* The artboard labels the fields with their placeholder alone. A
              placeholder is gone the moment someone types, so the label is
              real and only visually hidden. */}
          <label htmlFor="login-email" className="sr-only">
            E-Mail
          </label>
          <FxTextField
            id="login-email"
            onWhite
            placeholder="E-Mail"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="login-password" className="sr-only">
            Passwort
          </label>
          <FxTextField
            id="login-password"
            onWhite
            placeholder="Passwort"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <Link className="self-end text-small text-primary-text" to="/passwort-vergessen">
            Passwort vergessen?
          </Link>
        </div>

        <FxButton type="submit" block disabled={busy}>
          {busy ? 'Einloggen …' : 'Einloggen'}
        </FxButton>
      </form>

      {providers.length > 0 && (
        <>
          <OrDivider />
          <OAuthButtons
            providers={providers}
            returnTo={returnTo}
            disabled={busy}
            onGoogleCredential={(idToken) => void run(() => signInWithGoogleToken(idToken))}
          />
        </>
      )}
    </AuthScaffold>
  )
}
